import type { ReactNode } from 'react'

// The one reusable, self-describing list row. No column headers anywhere —
// each entry carries its own structure: a prominent title, one or two muted
// meta lines, an optional trailing status (accent dot + label) and optional
// action buttons. Renders coherently in light, dark and mobile.

export type EntryAccent = 'green' | 'orange' | 'red' | 'neutral'

// Palette (locked): green = confirmed/completed, orange = waiting/pending,
// red = cancelled/no-show/overdue. neutral = no state worth coloring.
const dotColor: Record<EntryAccent, string> = {
  green:   'bg-green-600',
  orange:  'bg-orange-500',
  red:     'bg-red-600',
  neutral: 'bg-zinc-300',
}

export interface EntryProps {
  /** Prominent first line. */
  title: ReactNode
  /** Up to two muted meta lines beneath the title. */
  meta?: ReactNode
  submeta?: ReactNode
  /** Trailing status: an accent dot plus an optional label. */
  accent?: EntryAccent
  status?: ReactNode
  /** Trailing action buttons. Clicks here never trigger the row onClick. */
  actions?: ReactNode
  /** When set, the whole row is clickable (navigates). */
  onClick?: () => void
}

export function Entry({ title, meta, submeta, accent, status, actions, onClick }: EntryProps) {
  return (
    <div
      data-testid="entry"
      onClick={onClick}
      className={`flex items-center gap-3 py-3 border-b border-zinc-100 ${
        onClick ? 'cursor-pointer hover:bg-zinc-50' : ''
      }`}
    >
      <div className="min-w-0 flex-1">
        <div className="truncate text-sm font-medium text-zinc-900">{title}</div>
        {meta && <div className="mt-0.5 truncate text-xs text-zinc-500">{meta}</div>}
        {submeta && <div className="truncate text-xs text-zinc-400">{submeta}</div>}
      </div>

      {(status != null || accent) && (
        <div className="flex shrink-0 items-center gap-1.5 text-xs text-zinc-500">
          {accent && <span className={`h-2 w-2 rounded-full ${dotColor[accent]}`} />}
          {status}
        </div>
      )}

      {actions && (
        <div className="flex shrink-0 items-center gap-1.5" onClick={(e) => e.stopPropagation()}>
          {actions}
        </div>
      )}
    </div>
  )
}

export function EntryList({
  children,
  isEmpty,
  emptyMessage,
}: {
  children: ReactNode
  isEmpty?: boolean
  emptyMessage?: string
}) {
  if (isEmpty) {
    return (
      <p className="py-12 text-center text-sm text-zinc-400">
        {emptyMessage ?? 'Nothing here yet.'}
      </p>
    )
  }
  return <div className="flex flex-col">{children}</div>
}
